<template>
  <div class="queue-dashboard">
    <header class="queue-dashboard__head">
      <div>
        <h1>Queues</h1>
        <p>Waiting work, active jobs, and the last four hours.</p>
      </div>
      <label class="queue-refresh">
        Refresh
        <input
          type="range"
          min="1"
          max="30"
          v-model.number="refreshSeconds"
          @change="resetRefresh"
        >
        <span>{{ refreshSeconds }}s</span>
      </label>
    </header>

    <p v-if="errorMessage" class="queue-dashboard__error">{{ errorMessage }}</p>

    <div v-if="dashboard">
      <ul class="queue-metrics">
        <li v-for="metric in metrics" :key="metric.label">
          <span class="queue-metrics__value" :class="{ 'is-alert': metric.alert && metric.value > 0 }">{{ metric.value }}</span>
          <span class="queue-metrics__label">{{ metric.label }}</span>
        </li>
      </ul>

      <p class="queue-dashboard__sample" v-if="sampledLabel">Last sample {{ sampledLabel }}</p>

      <section class="queue-history">
        <h2>History</h2>
        <p v-if="historyPoints.length < 2">History appears after a couple of minute samples.</p>
        <svg v-else viewBox="0 0 640 140" width="100%" height="140" role="img" aria-label="Queue history">
          <polyline fill="none" stroke="#3d4f8a" stroke-width="2" :points="linePoints('waiting')"></polyline>
          <polyline fill="none" stroke="#2f7d4a" stroke-width="2" :points="linePoints('active')"></polyline>
          <polyline fill="none" stroke="#b42318" stroke-width="2" :points="linePoints('failed')"></polyline>
        </svg>
        <p class="queue-history__legend">
          <span class="is-waiting">Waiting</span>
          <span class="is-active">Active</span>
          <span class="is-failed">Failed</span>
        </p>
      </section>

      <section class="queue-runtime">
        <h2>Workers</h2>
        <p v-if="!runtime.available">Runtime metrics are not available from Prometheus.</p>
        <p v-else-if="runtime.pods.length === 0">No worker samples yet.</p>
        <table v-else class="queue-table">
          <thead>
            <tr>
              <th>Pod</th>
              <th class="num">Memory</th>
              <th class="num">Limit</th>
              <th class="num">CPU</th>
              <th class="num">Restarts</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="pod in runtime.pods" :key="pod.pod">
              <td>{{ pod.pod }}</td>
              <td class="num" :class="{ 'is-alert': memoryHigh(pod) }">{{ formatBytes(pod.memoryBytes) }}</td>
              <td class="num">{{ formatBytes(pod.memoryLimitBytes) }}</td>
              <td class="num">{{ formatCpu(pod.cpuCores) }}</td>
              <td class="num" :class="{ 'is-alert': pod.restarts > 0 }">{{ pod.restarts }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <table class="queue-table">
        <thead>
          <tr>
            <th>Queue</th>
            <th class="num">Waiting</th>
            <th class="num">Active</th>
            <th class="num">Delayed</th>
            <th class="num">Failed</th>
            <th class="num">Completed</th>
            <th class="num">Workers</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="queue in dashboard.queues" :key="queue.name">
            <td>{{ labelFor(queue.name) }}</td>
            <td class="num">{{ queue.waiting }}</td>
            <td class="num">{{ queue.active }}</td>
            <td class="num">{{ queue.delayed }}</td>
            <td class="num" :class="{ 'is-alert': queue.failed > 0 }">{{ queue.failed }}</td>
            <td class="num">{{ queue.completed }}</td>
            <td class="num">{{ queue.workers }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import apollo from "@/apollo.js"
import gql from "graphql-tag"

const QUEUE_LABELS = {
  system: "System",
  batchUpdateCase: "Batch update cases",
  syncNestedObjectCase: "Nested case refresh",
  cronhooks: "Cronhooks",
  cronhooksAdminQueue: "Cronhook admin",
  notificationQueue: "Notifications",
  internalHousekeepingQueue: "Housekeeping",
  messageSentimentJob: "Message sentiment",
  chatResponseJob: "Chat response",
  chatSessionNameJob: "Chat session names",
  chatMessageSummaryJob: "Message summaries",
}

const DASHBOARD_QUERY = gql(`
  query queueDashboard {
    queueDashboard {
      sampledAt
      totals { waiting active delayed failed completed paused workers }
      queues { name waiting active delayed failed completed paused workers }
      history { at waiting active delayed failed }
      runtime {
        available
        pods { pod memoryBytes memoryLimitBytes cpuCores restarts }
      }
    }
  }
`)

export default {
  name: "QueueDashboard",
  data: () => ({
    dashboard: null,
    errorMessage: "",
    refreshTimer: null,
    refreshSeconds: 15,
  }),
  computed: {
    metrics() {
      const totals = this.dashboard && this.dashboard.totals ? this.dashboard.totals : {}
      return [
        { label: "Waiting", value: totals.waiting || 0 },
        { label: "Active", value: totals.active || 0 },
        { label: "Delayed", value: totals.delayed || 0 },
        { label: "Failed", value: totals.failed || 0, alert: true },
        { label: "Paused", value: totals.paused || 0 },
        { label: "Workers", value: totals.workers || 0 },
      ]
    },
    historyPoints() {
      return this.dashboard && this.dashboard.history ? this.dashboard.history : []
    },
    runtime() {
      const runtime = this.dashboard && this.dashboard.runtime
      if (runtime) return runtime
      return { available: false, pods: [] }
    },
    sampledLabel() {
      if (!this.dashboard || !this.dashboard.sampledAt) return ""
      const parsed = new Date(this.dashboard.sampledAt)
      if (isNaN(parsed.getTime())) return this.dashboard.sampledAt
      return parsed.toLocaleString()
    },
  },
  methods: {
    labelFor(name) {
      return QUEUE_LABELS[name] || name
    },
    formatBytes(value) {
      const amount = Number(value)
      if (!isFinite(amount) || amount <= 0) return "—"
      return (amount / 1024 / 1024 / 1024).toFixed(2) + " GiB"
    },
    formatCpu(cores) {
      const amount = Number(cores)
      if (!isFinite(amount) || amount <= 0) return "0"
      if (amount < 1) return Math.round(amount * 1000) + "m"
      return amount.toFixed(2)
    },
    memoryHigh(pod) {
      const used = Number(pod && pod.memoryBytes)
      const limit = Number(pod && pod.memoryLimitBytes)
      if (!isFinite(used) || !isFinite(limit) || limit <= 0) return false
      return used / limit >= 0.85
    },
    linePoints(field) {
      const points = this.historyPoints
      if (points.length < 2) return ""
      let max = 1
      points.forEach((point) => {
        const value = Number(point[field]) || 0
        if (value > max) max = value
      })
      const width = 640
      const height = 140
      const step = width / (points.length - 1)
      return points.map((point, index) => {
        const value = Number(point[field]) || 0
        const x = Math.round(index * step)
        const y = Math.round(height - 10 - (value / max) * (height - 20))
        return x + "," + y
      }).join(" ")
    },
    loadDashboard() {
      const self = this
      return apollo.query({
        query: DASHBOARD_QUERY,
        fetchPolicy: "network-only",
      }).then((response) => {
        self.dashboard = response && response.data ? response.data.queueDashboard : null
        self.errorMessage = ""
      }).catch((error) => {
        self.errorMessage = error && error.message ? error.message : "Unable to load queues"
      })
    },
    resetRefresh() {
      if (this.refreshTimer) clearInterval(this.refreshTimer)
      const seconds = Number(this.refreshSeconds)
      const interval = seconds >= 1 && seconds <= 30 ? seconds : 15
      this.refreshTimer = setInterval(() => {
        this.loadDashboard()
      }, interval * 1000)
    },
  },
  mounted() {
    this.loadDashboard()
    this.resetRefresh()
  },
  beforeDestroy() {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
  },
}
</script>

<style scoped>
.queue-dashboard {
  background: #fff;
  color: #1c1c1c;
  min-height: 100%;
  padding: 28px 32px 48px;
}

.queue-dashboard__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
}

.queue-refresh {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: #5f6b7a;
  font-size: 12px;
  white-space: nowrap;
}

.queue-refresh input {
  width: 140px;
}

.queue-dashboard__head h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  line-height: 1.2;
}

.queue-dashboard__head p,
.queue-dashboard__sample,
.queue-history p,
.queue-runtime p {
  margin: 6px 0 0;
  color: #5f6b7a;
  font-size: 14px;
}

.queue-dashboard__error {
  margin: 20px 0 0;
  color: #b42318;
}

.queue-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 28px 40px;
  list-style: none;
  margin: 28px 0 0;
  padding: 0 0 20px;
  border-bottom: 1px solid #e6e8eb;
}

.queue-metrics__value {
  display: block;
  font-size: 28px;
  font-weight: 600;
  line-height: 1.1;
}

.queue-metrics__label {
  display: block;
  margin-top: 4px;
  color: #5f6b7a;
  font-size: 12px;
}

.queue-runtime,
.queue-history {
  margin-top: 28px;
}

.queue-runtime h2,
.queue-history h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.queue-history svg {
  display: block;
  margin-top: 12px;
}

.queue-history__legend span {
  margin-right: 16px;
}

.queue-history__legend .is-waiting { color: #3d4f8a; }
.queue-history__legend .is-active { color: #2f7d4a; }
.queue-history__legend .is-failed { color: #b42318; }

.queue-table {
  width: 100%;
  margin-top: 32px;
  border-collapse: collapse;
}

.queue-table th,
.queue-table td {
  padding: 12px 16px 12px 0;
  text-align: left;
  border-bottom: 1px solid #e6e8eb;
}

.queue-table th {
  color: #5f6b7a;
  font-size: 12px;
  font-weight: 600;
}

.queue-table .num {
  text-align: right;
}

.queue-table .is-alert,
.queue-metrics__value.is-alert {
  color: #b42318;
}
</style>
